import { SolutionScenario } from '@/types/scenarios/solution';
import { Tv, Phone, Calendar, Wrench, Truck, HelpCircle, Shield, RefreshCw } from 'lucide-react';

export const appliances: SolutionScenario = {
  meta: {
    id: 'appliances',
    slug: 'appliances',
    name: 'Appliances',
    tagline: 'Genies that troubleshoot, schedule, and answer every appliance call',
    description: "A genie that walks customers through setup and common fixes, books delivery and repairs, and handles the same product, parts, and warranty questions every day.",
    market: 'Appliances',
    subMarkets: ['Appliance Retailers', 'Repair Services', 'Manufacturers', 'Extended Warranty Providers', 'Parts Suppliers'],
    specificNeeds: [
      'Product availability and feature inquiries',
      'Delivery and installation scheduling',
      'Repair appointment booking',
      'Warranty claim processing',
      'Parts ordering assistance',
      'Troubleshooting support'
    ],
    commonRoles: ['Sales Associates', 'Service Technicians', 'Customer Service Reps', 'Delivery Coordinators'],
    marketSize: '$250 billion US market',
    isFeatured: true
  },

  hero: {
    headline: 'Always on for every appliance',
    headlineGradient: 'Always on',
    headlineRest: 'for every appliance',
    subheadline: "A genie that troubleshoots, walks customers through setup and repairs, books service and deliveries, and handles the same product and warranty questions every day so your team isn't fielding the basics.",
  },

  recommendedPlaybooks: [
    'lead-capture',
    'appointment-scheduler',
    'equipment-troubleshooting',
    'interactive-faq'
  ],

  applications: [
    {
      id: 'product-inquiries',
      name: 'Product Sales & Inquiries',
      description: 'Answer questions about appliances, features, pricing, and availability',
      department: 'Sales',
      icon: Phone,
      playbookIds: ['lead-capture', 'interactive-faq'],
      benefits: [
        '24/7 product information',
        'Inventory availability checks',
        'Feature comparison assistance',
        'Price matching inquiries'
      ]
    },
    {
      id: 'delivery-installation',
      name: 'Delivery & Installation Scheduling',
      description: 'Coordinate delivery windows and installation appointments',
      department: 'Operations',
      icon: Truck,
      playbookIds: ['appointment-scheduler'],
      benefits: [
        'Self service scheduling',
        'Real-time availability',
        'Automated confirmations',
        'Rescheduling made easy'
      ]
    },
    {
      id: 'repair-service',
      name: 'Repair Service Booking',
      description: 'Schedule repair appointments with intelligent routing to technicians',
      department: 'Service',
      icon: Wrench,
      playbookIds: ['appointment-scheduler'],
      benefits: [
        'Symptom-based scheduling',
        'Technician skill matching',
        'Parts pre-ordering',
        'Same-day service options'
      ]
    },
    {
      id: 'troubleshooting',
      name: 'Troubleshooting Support',
      description: 'Guide customers through common issues before dispatching service',
      department: 'Support',
      icon: HelpCircle,
      playbookIds: ['equipment-troubleshooting'],
      benefits: [
        'Resolve simple issues instantly',
        'Reduce unnecessary service calls',
        'Improve first-call resolution',
        'Customer empowerment'
      ]
    },
    {
      id: 'warranty-claims',
      name: 'Warranty & Protection Plans',
      description: 'Handle warranty inquiries, coverage verification, and protection plan enrollment',
      department: 'Customer Service',
      icon: Shield,
      playbookIds: ['interactive-faq'],
      benefits: [
        'Instant coverage verification',
        'Claim initiation support',
        'Protection plan upsells',
        'Policy explanation'
      ]
    },
    {
      id: 'parts-exchange',
      name: 'Parts & Exchange Requests',
      description: 'Process parts orders and handle product exchange or return inquiries',
      department: 'Parts',
      icon: RefreshCw,
      playbookIds: ['interactive-faq', 'appointment-scheduler'],
      benefits: [
        'Parts availability checks',
        'Exchange eligibility verification',
        'Return initiation',
        'Order status updates'
      ]
    }
  ],

  challenges: [
    {
      title: 'High Call Volume',
      description: 'Service and support lines overwhelmed with routine inquiries',
      impact: 'high',
      affectedAreas: ['Customer satisfaction', 'Staff burnout', 'Response times']
    },
    {
      title: 'Scheduling Complexity',
      description: 'Coordinating delivery windows, technician availability, and customer schedules',
      impact: 'high',
      affectedAreas: ['Operational efficiency', 'Customer experience', 'Technician utilization']
    },
    {
      title: 'After Hours Support Needs',
      description: 'Appliance issues don\'t wait for business hours',
      impact: 'medium',
      affectedAreas: ['Customer satisfaction', 'Emergency response', 'Competitive advantage']
    },
    {
      title: 'Parts and Warranty Questions',
      description: 'Customers need quick answers about warranties, parts, and coverage',
      impact: 'medium',
      affectedAreas: ['Customer frustration', 'Call duration', 'Support costs']
    }
  ],

  solutions: [
    {
      challenge: 'Overwhelming call volume',
      solution: 'Voice Genie handles routine inquiries and scheduling',
      implementation: 'Deploy for product questions, delivery scheduling, and basic support',
      expectedResults: [
        '65% reduction in routine call handling',
        'Average wait time under 30 seconds',
        'Staff focus on complex issues'
      ]
    },
    {
      challenge: 'Inefficient service scheduling',
      solution: 'Intelligent appointment booking with symptom analysis',
      implementation: 'Connect to service calendar with technician skill matching',
      expectedResults: [
        '40% improvement in first-time fix rates',
        'Optimized technician routes',
        '25% more daily appointments'
      ]
    },
    {
      challenge: 'After hours emergencies',
      solution: '24/7 Voice Genie support for troubleshooting and urgent booking',
      implementation: 'Always on Voice Genie with emergency escalation protocols',
      expectedResults: [
        'Round-the-clock customer support',
        '30% of issues resolved via troubleshooting',
        'Emergency callbacks scheduled automatically'
      ]
    }
  ],

  integrations: {
    marketSpecific: [
      {
        name: 'ServiceTitan',
        category: 'Field Service',
        description: 'Leading field service management platform',
        required: false,
        popularityScore: 90
      },
      {
        name: 'Housecall Pro',
        category: 'Field Service',
        description: 'Home service business software',
        required: false,
        popularityScore: 85
      },
      {
        name: 'ServiceMax',
        category: 'Field Service',
        description: 'Enterprise field service management',
        required: false,
        popularityScore: 82
      },
      {
        name: 'Jobber',
        category: 'Scheduling',
        description: 'Service business scheduling software',
        required: false,
        popularityScore: 80
      }
    ],
    common: [
      {
        name: 'Salesforce',
        category: 'CRM',
        description: 'Customer relationship management',
        required: false,
        popularityScore: 95
      },
      {
        name: 'Zendesk',
        category: 'Support',
        description: 'Customer support platform',
        required: false,
        popularityScore: 88
      }
    ]
  },

  metrics: {
    averageImplementationTime: '1-2 weeks',
    typicalROI: '350%',
    adoptionRate: '87% customer satisfaction',
    customerSatisfaction: '4.6/5'
  },

  demoConfig: {
    firstMessage: "Hello! Thanks for calling. I can help you with product information, schedule a delivery or installation, book a repair appointment, or troubleshoot an issue. What can I help you with today?",
    systemPrompt: "You are a helpful appliance industry assistant. Help customers with product inquiries, schedule deliveries and repairs, and guide them through basic troubleshooting. Be knowledgeable about common appliance brands and issues. Always aim to resolve the customer's need efficiently.",
    marketContext: "Appliance retailer or service provider handling sales and service inquiries",
    exampleScenarios: [
      "I need to schedule a refrigerator repair",
      "When can you deliver my new washer?",
      "My dishwasher won't drain, can you help?",
      "Is the Samsung range in stock?"
    ]
  },

  caseStudies: [
    {
      companyName: 'HomeServe Appliances',
      companySize: '12 service locations',
      location: 'Midwest United States',
      challenge: 'Call center overwhelmed, 8+ minute average wait times, 25% call abandonment',
      implementation: 'Deployed Help Genie for service scheduling, troubleshooting, and parts inquiries',
      timeline: '2 weeks',
      results: [
        {
          metric: 'Wait Time',
          value: '< 45 sec',
          improvement: 'From 8+ minutes'
        },
        {
          metric: 'Call Abandonment',
          value: '4%',
          improvement: 'From 25%'
        },
        {
          metric: 'Self-Resolved Issues',
          value: '28%',
          improvement: 'Via troubleshooting'
        },
        {
          metric: 'Service Appointments',
          value: '+22%',
          improvement: 'Monthly increase'
        }
      ],
      quote: {
        text: "Our customers used to dread calling us. Now they get immediate help, and many issues are resolved without even needing a technician visit. It's transformed our business.",
        author: 'Sarah Martinez',
        title: 'Director of Customer Service'
      }
    }
  ],

  resources: [
    {
      type: 'guide',
      title: 'Appliance Industry Voice Genie Guide',
      description: 'Implementing Voice Genies for appliance retailers and service providers',
      url: '/resources/appliance-voice-guide'
    },
    {
      type: 'webinar',
      title: 'Reducing Service Call Volume with Help Genie',
      description: 'How leading appliance companies cut costs while improving satisfaction',
      url: '/webinars/appliance-service-ai'
    },
    {
      type: 'template',
      title: 'Appliance Troubleshooting Scripts',
      description: 'Pre-built voice scripts for common appliance issues',
      url: '/templates/appliance-troubleshooting'
    }
  ],

  faqs: [
    {
      question: 'Can the genie troubleshoot specific appliance issues?',
      answer: 'Yes, Help Genie can guide customers through common troubleshooting steps for major appliance types and brands. It asks diagnostic questions and provides step-by-step guidance, often resolving issues without requiring a service call.'
    },
    {
      question: 'How does it handle warranty and parts questions?',
      answer: 'The system can be connected to your warranty database and parts inventory to provide accurate information about coverage and parts availability. Complex warranty claims can be escalated to your team.'
    },
    {
      question: 'Does it integrate with our field service software?',
      answer: 'Yes, Help Genie integrates with popular field service platforms like ServiceTitan, Housecall Pro, and ServiceMax for seamless appointment booking and technician dispatch.'
    }
  ],

  // Dynamic page content for the solution landing page
  pageContent: {
    scenariosHeader: {
      title: "Never miss a service call\neven when you're closed",
      subtitle: "See how your Genie handles appliance inquiries, schedules repairs, and keeps customers happy — even at 2am when the fridge stops working."
    },
    chatScenarios: [
      {
        id: 'emergency_repair',
        title: 'Emergency Repair',
        messages: [
          { type: 'user', text: "My refrigerator stopped cooling and it's full of food!", delay: 300 },
          { type: 'genie', text: "I understand how stressful that is. Let me help you right away. First, can I get your name?", delay: 1200 },
          { type: 'user', text: "Sarah Johnson", delay: 2500 },
          { type: 'genie', text: "Thanks Sarah. Have you checked if the temperature dial was accidentally bumped? Sometimes that's the quick fix.", delay: 3400 },
          { type: 'user', text: "I checked, it's on the right setting but still not cooling", delay: 4600 },
          { type: 'genie', text: "Got it. I can get a technician out first thing tomorrow morning between 8-10am. What's the best phone number to reach you?", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Customer details captured', delay: 6500 },
          { id: '2', text: 'Quick troubleshooting attempted', delay: 6700 },
          { id: '3', text: 'Marked as urgent service', delay: 6900 },
          { id: '4', text: 'Morning appointment scheduled', delay: 7100 },
          { id: '5', text: 'Technician notified', delay: 7300 },
        ],
      },
      {
        id: 'delivery_inquiry',
        title: 'Delivery Scheduling',
        messages: [
          { type: 'user', text: "I just bought a washer and dryer, when can you deliver?", delay: 300 },
          { type: 'genie', text: "Congratulations on your purchase! I'd be happy to help schedule delivery. Can I have your order number?", delay: 1200 },
          { type: 'user', text: "It's #45892", delay: 2500 },
          { type: 'genie', text: "Perfect, I found your order. We have delivery slots available this Thursday or Saturday. Which works better for you?", delay: 3400 },
          { type: 'user', text: "Saturday would be great", delay: 4600 },
          { type: 'genie', text: "I have 9am-12pm or 1pm-4pm on Saturday. The team will also handle installation and haul away your old units.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Order verified', delay: 6500 },
          { id: '2', text: 'Availability checked', delay: 6700 },
          { id: '3', text: 'Saturday delivery scheduled', delay: 6900 },
          { id: '4', text: 'Installation confirmed', delay: 7100 },
          { id: '5', text: 'Haul-away arranged', delay: 7300 },
        ],
      },
      {
        id: 'troubleshooting',
        title: 'Troubleshooting',
        messages: [
          { type: 'user', text: "My dishwasher won't drain properly", delay: 300 },
          { type: 'genie', text: "I can help troubleshoot that. Is there standing water at the bottom of the dishwasher?", delay: 1200 },
          { type: 'user', text: "Yes, about 2 inches of water", delay: 2500 },
          { type: 'genie', text: "Let's check the filter first. It's usually at the bottom — can you remove it and see if there's any debris?", delay: 3400 },
          { type: 'user', text: "Oh wow, there was a bunch of food stuck in there!", delay: 4600 },
          { type: 'genie', text: "That's often the culprit! Clean it out, replace the filter, and run a rinse cycle. Let me know if that fixes it.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Issue identified', delay: 6500 },
          { id: '2', text: 'Guided troubleshooting provided', delay: 6700 },
          { id: '3', text: 'Root cause found', delay: 6900 },
          { id: '4', text: 'Issue resolved without service call', delay: 7100 },
          { id: '5', text: 'Service call avoided — $150 saved', delay: 7300 },
        ],
      },
      {
        id: 'warranty_check',
        title: 'Warranty Questions',
        messages: [
          { type: 'user', text: "Is my oven still under warranty? It's making a weird noise", delay: 300 },
          { type: 'genie', text: "I can check that for you. What's the model number? It's usually on a sticker inside the door.", delay: 1200 },
          { type: 'user', text: "NE59R6631SS", delay: 2500 },
          { type: 'genie', text: "Thanks. And when did you purchase it?", delay: 3400 },
          { type: 'user', text: "About 14 months ago", delay: 4600 },
          { type: 'genie', text: "Good news — that model has a 2-year manufacturer warranty on parts. I can schedule a covered repair visit for you.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Model identified', delay: 6500 },
          { id: '2', text: 'Purchase date confirmed', delay: 6700 },
          { id: '3', text: 'Warranty verified — covered', delay: 6900 },
          { id: '4', text: 'Service scheduled', delay: 7100 },
          { id: '5', text: 'Customer cost: $0', delay: 7300 },
        ],
      },
      {
        id: 'product_inquiry',
        title: 'Product Questions',
        messages: [
          { type: 'user', text: "Do you have the LG French door refrigerator in stock?", delay: 300 },
          { type: 'genie', text: "I'd be happy to check for you. Which model are you looking at? Or I can tell you what's available.", delay: 1200 },
          { type: 'user', text: "The one with the InstaView door, around $2500", delay: 2500 },
          { type: 'genie', text: "That's the LRMVS3006S. We have 3 in stock — stainless steel. Would you like me to have someone call you about financing options?", delay: 3400 },
          { type: 'user', text: "Yes please, and do you offer installation?", delay: 4600 },
          { type: 'genie', text: "We do! Free delivery and installation with purchase. I'll have our sales team reach out tomorrow with all the details.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Product identified', delay: 6500 },
          { id: '2', text: 'Inventory confirmed — in stock', delay: 6700 },
          { id: '3', text: 'Services explained', delay: 6900 },
          { id: '4', text: 'Sales follow-up scheduled', delay: 7100 },
          { id: '5', text: 'Potential sale: $2,500+', delay: 7300 },
        ],
      },
    ],
    useCasesHeader: {
      title: "Stop Losing Customers to Voicemail",
      subtitle: "Appliance issues don't wait for business hours. See how your Genie keeps customers happy when they need help most."
    },
    useCaseCards: [
      {
        id: 'retailers',
        title: 'Appliance Retailers',
        description: "Never miss a sale. Answer product questions, check inventory, and schedule deliveries around the clock.",
        bullets: [
          'Answer product and pricing questions 24/7',
          'Check real-time inventory availability',
          'Schedule delivery and installation',
          'Capture leads when store is closed',
        ],
        image: '/solutions-pages/appliances/usecase-retail-sales.webp',
      },
      {
        id: 'repair_services',
        title: 'Repair Services',
        description: "Emergencies don't wait. Book service calls, triage urgent issues, and route technicians efficiently.",
        bullets: [
          'Book emergency and routine repairs',
          'Triage calls by urgency',
          'Match issues to right technicians',
          'Reduce after hours callbacks',
        ],
        image: '/solutions-pages/appliances/usecase-repair-booking.webp',
      },
      {
        id: 'manufacturers',
        title: 'Manufacturers',
        description: "Support customers at scale. Handle warranty questions, troubleshooting, and parts inquiries.",
        bullets: [
          'Verify warranty coverage instantly',
          'Guide customers through troubleshooting',
          'Process parts orders',
          'Escalate complex issues to specialists',
        ],
        image: '/solutions-pages/appliances/usecase-manufacturer-support.webp',
      },
      {
        id: 'warranty_providers',
        title: 'Warranty Providers',
        description: "Reduce claim processing time. Verify coverage, initiate claims, and schedule service in one call.",
        bullets: [
          'Instant coverage verification',
          'Streamlined claim initiation',
          'Authorized service provider routing',
          'Claim status updates',
        ],
        image: '/solutions-pages/appliances/usecase-warranty-claims.webp',
      },
    ],
    finalCTA: {
      title: "Appliances Break at the Worst Times",
      subtitle: "When the fridge dies at midnight or the washer floods at 6am, your Genie is there — capturing the call, calming the customer, and scheduling the fix.",
      pillars: [
        {
          title: "Always\nResponding",
          gradient: "from-purple-400 to-blue-400",
          description: "Emergency repairs scheduled instantly, even at 2am.",
        },
        {
          title: "Always\nSolving",
          gradient: "from-blue-400 to-cyan-400",
          description: "30% of issues resolved through guided troubleshooting.",
        },
        {
          title: "Always\nSelling",
          gradient: "from-cyan-400 to-teal-400",
          description: "Every inquiry captured. No lead goes to voicemail.",
        },
      ],
      backgroundImage: '/solutions-pages/appliances/cta-background.webp',
    },
  },
};
